import { useMutation } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { CREATE_TAG_MUTATION } from '@/modules/common/mutations'
import type {
  CreateTagInput,
  CreateTagMutationResponse,
  TagNode,
} from '@/modules/common/types'
import type { IssueTagForm } from '@/modules/companies/CompanySettings/components/IssueCategorySettings/issue-tag-prompt'
import { IssueTagPrompt } from '@/modules/companies/CompanySettings/components/IssueCategorySettings/issue-tag-prompt'
import { useOnErrorAuto } from '@/utils/hooks'

/**
 * Bundles the "create a new Issue tag" interaction: open the IssueTagPrompt
 * modal, run the createTag mutation, return the new tag id so the caller can
 * add it to a selection set.
 */
export function useIssueTagCreate(existingTags: TagNode[]) {
  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const [createTag] = useMutation<
    CreateTagMutationResponse,
    { input: CreateTagInput }
  >(CREATE_TAG_MUTATION, {
    refetchQueries: ['AllTags'],
    awaitRefetchQueries: true,
    onError: onErrorAuto(),
  })

  async function prompt(): Promise<string | null> {
    const { data: result } = await addPrompt<IssueTagForm | null>(resolve => (
      <IssueTagPrompt
        existingTags={existingTags.map(t => ({ id: t.id, name: t.name }))}
        onSubmit={resolve}
      />
    ))
    if (result === null) return null

    const { data: mutationResult } = await createTag({
      variables: {
        input: {
          name: result.name,
          color: result.color,
          description: result.description,
          scope: 'ISSUE',
        },
      },
    })
    return mutationResult?.createTag?.tag?.id ?? null
  }

  return { prompt }
}
